import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";

const STORAGE_KEY = "openstore_compare";
const MAX_COMPARE = 4;
const BASE_URL = import.meta.env.BASE_URL || "/";

interface CompareContextType {
  compareIds: number[];
  isInCompare: (productId: number) => boolean;
  toggleCompare: (productId: number) => boolean;
  removeFromCompare: (productId: number) => void;
  clearCompare: () => void;
  compareCount: number;
  maxCompare: number;
}

const CompareContext = createContext<CompareContextType>({
  compareIds: [],
  isInCompare: () => false,
  toggleCompare: () => false,
  removeFromCompare: () => {},
  clearCompare: () => {},
  compareCount: 0,
  maxCompare: MAX_COMPARE,
});

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareIds, setCompareIds] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const prevIdsRef = useRef<number[]>([]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(compareIds));

    if (compareIds.length >= 2 && compareIds.length > prevIdsRef.current.length) {
      fetch(`${BASE_URL}api/comparison-logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ productIds: compareIds }),
      }).catch(() => {});
    }
    prevIdsRef.current = compareIds;
  }, [compareIds]);

  const isInCompare = useCallback(
    (productId: number) => compareIds.includes(productId),
    [compareIds]
  );

  const toggleCompare = useCallback(
    (productId: number): boolean => {
      if (compareIds.includes(productId)) {
        setCompareIds((prev) => prev.filter((id) => id !== productId));
        return false;
      }
      if (compareIds.length >= MAX_COMPARE) {
        return false;
      }
      setCompareIds((prev) => [...prev, productId]);
      return true;
    },
    [compareIds]
  );

  const removeFromCompare = useCallback((productId: number) => {
    setCompareIds((prev) => prev.filter((id) => id !== productId));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareIds([]);
  }, []);

  return (
    <CompareContext.Provider
      value={{
        compareIds,
        isInCompare,
        toggleCompare,
        removeFromCompare,
        clearCompare,
        compareCount: compareIds.length,
        maxCompare: MAX_COMPARE,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  return useContext(CompareContext);
}
